<script setup lang="ts">
import { onClickOutside, useVModel } from '@vueuse/core'

import { ITab } from '~/types/tab.type'

interface IProps {
  modelValue: unknown;
  options: Array<ITab<unknown>>;
  bold?: boolean;
  name?: string;
  placeholder?: string;
}

const props = defineProps<IProps>()

const emits = defineEmits([ 'update:modelValue', 'change' ])

const showOptionList = ref<boolean>(false)
const optionListRef = ref(null)

const selectModel = useVModel(props, 'modelValue')

const setShowOptionList = (value = !showOptionList.value) => {
  showOptionList.value = value
}

const change = (newValue: unknown) => {
  selectModel.value = newValue
  setShowOptionList(false)
  emits('change', newValue)
}

onClickOutside(optionListRef, () => {
  setShowOptionList(false)
})
</script>

<template>
  <div
    class="select-container"
    :class="{active: showOptionList, bold}"
    @mousedown.prevent="setShowOptionList"
  >
    <select
      v-model="selectModel"
      class="select"
      :class="{active: showOptionList, placeholder: placeholder && selectModel === null}"
      :name="name"
    >
      <option
        v-if="placeholder"
        :value="null"
        disabled
      >
        {{ placeholder }}
      </option>
      <option
        v-for="option in options"
        :key="String(option.value)"
        :value="option.value"
        class="hidden"
      >
        {{ option.label }}
      </option>
    </select>
    <FontAwesomeIcon
      class="icon"
      icon="chevron-down"
      size="lg"
    />
    <ul
      v-if="showOptionList"
      class="option-list"
      ref="optionListRef"
    >
      <li
        v-for="option in options"
        :key="String(option.value)"
        class="option"
        :class="{selected: option.value === selectModel}"
        @click="change(option.value)"
      >
        {{ option.label }}
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.select-container {
  @apply inline-flex items-center gap-x-2 p-2 relative rounded-lg z-20 justify-between;
  transition: all .3s ease;

  &.active {
    @apply z-30;
  }

  &.active, &:hover {
    @apply bg-primary/5 cursor-pointer;

    .icon {
      @apply text-primary;
      transition: all .3s ease;
    }
  }

  &.bold {
    @apply font-bold;
  }

  .select {
    @apply text-primary bg-transparent outline-none cursor-pointer;
    -webkit-appearance: none;
    -moz-appearance: none;
    font-feature-settings: inherit;

    &.placeholder {
      @apply text-primary/40;
    }
  }

  .option-list {
    @apply absolute left-0 top-[calc(100%+0.5rem)] flex w-full flex-col overflow-hidden rounded-lg text-primary shadow-[0_32px_64px_0] shadow-background/20 leading-normal bg-primary/5 backdrop-blur-lg;

    .option {
      @apply p-2 text-primary/40 cursor-pointer transition-all duration-300 ease-[ease];

      &:not(.selected):hover {
        @apply bg-primary/10 text-primary;
      }

      &.selected {
        @apply bg-primary/5 text-primary;
      }
    }
  }

  .icon {
    @apply text-primary/40 group-hover:text-primary group-focus-visible:text-primary;
  }
}
</style>